import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import QRCodeWithLogo from "./QRCodeLogo";

// Modal de download do QR Code de uma coleção, com opção de sobrepor um logo.
// Usado em CollectionListPage (botão "Download") e em CreateQrCodePage (após criar).
// Reconstruído a partir do modal inline que existia em CollectionListPage antes
// do commit "Melhorias qrcode", cujo arquivo extraído não foi versionado.
function QrCodeDownloadModal({ collectionId, collectionName, qrColor, onClose }) {
  const [logoSrc, setLogoSrc] = useState(null);
  const previewRef = useRef(null);
  const logoInputRef = useRef(null);

  const qrText = `${window.location.origin}/collection/${collectionId}`;

  // Libera o object URL do logo anterior ao trocar de logo ou fechar o modal.
  useEffect(() => {
    return () => {
      if (logoSrc) URL.revokeObjectURL(logoSrc);
    };
  }, [logoSrc]);

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    setLogoSrc(file ? URL.createObjectURL(file) : null);
  };

  const removeLogo = () => {
    setLogoSrc(null);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const handleDownload = () => {
    const canvas = previewRef.current?.querySelector("canvas");
    if (!canvas) {
      toast.error("Ocorreu um erro ao tentar baixar o QR Code.");
      return;
    }
    const safeName = String(collectionName || collectionId)
      .replace(/[<>:"/|?*]+/g, "_")
      .trim();
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.setAttribute("download", `${safeName}_qrcode.png`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("O seu download foi iniciado!");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
        <h3 className="text-2xl font-bold mb-2 text-gray-900">Download QR Code</h3>
        {collectionName && (
          <p className="text-gray-500 text-sm mb-4 break-words">{collectionName}</p>
        )}
        <p className="text-gray-600 mb-6">Opcional: Escolha um logo.</p>

        <div ref={previewRef} className="mb-6 flex justify-center">
          <QRCodeWithLogo
            text={qrText}
            logoSrc={logoSrc}
            width={280}
            qrColor={qrColor || "#000000"}
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="logo-upload"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Selecione um Logo
          </label>
          <input
            type="file"
            id="logo-upload"
            accept="image/png, image/jpeg"
            onChange={handleLogoSelect}
            ref={logoInputRef}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {logoSrc && (
            <button
              type="button"
              onClick={removeLogo}
              className="mt-2 text-sm text-red-600 hover:text-red-800 transition"
            >
              Remover Logo
            </button>
          )}
        </div>

        <div className="flex justify-center space-x-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-6 rounded-lg transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition"
          >
            Fazer Download
          </button>
        </div>
      </div>
    </div>
  );
}

export default QrCodeDownloadModal;
